import { deviceIdName } from '@/constants/settings';
import { getClientLogger } from '@/lib/logger/pino-logger.client';
import { useEffect, useState } from 'react';

export interface IDevice {
  id: string;
  ip?: string;
  userAgent?: string;
  platform?: string;
  mobile?: string;
}

interface IDeviceProps {
  metadata?: boolean;
  skipFetch?: boolean;
}

const defaultDevice: IDevice = {
  id: '',
  ip: '',
  userAgent: '',
  platform: '',
  mobile: '',
};

export const useDevice = (props?: IDeviceProps) => {
  const [device, setDevice] = useState<IDevice | null>(null);
  const [isLoading, setIsLoading] = useState(!props?.skipFetch);

  const logger = getClientLogger('hooks.useDevice');

  const getDevice = async () => {
    setIsLoading(true);

    try {
      const response = await fetch('/api/device');

      if (!response.ok) {
        logger.error(`Error fetching device info: ${response.statusText}`);
        setIsLoading(false);
        return defaultDevice;
      }

      const _device: IDevice = await response.json();

      localStorage.setItem(deviceIdName, _device.id);

      setIsLoading(false);

      return _device;
    } catch (error) {
      logger.error(`Error fetching device info: ${(error as Error).message}`);
      setIsLoading(false);
      return defaultDevice;
    }
  };

  const getDeviceId = async () => {
    let id = localStorage.getItem(deviceIdName);

    if (!id) {
      const { id: deviceId } = await getDevice();
      id = deviceId;
    }

    return id;
  };

  const fetchData = async () => {
    let device: IDevice;

    if (props?.metadata) {
      device = await getDevice();
    } else {
      device = {
        id: await getDeviceId(),
      };
    }

    setDevice(device);

    return device;
  };

  useEffect(() => {
    if (props?.skipFetch) {
      return;
    }

    fetchData();
  }, []);

  return {
    device,
    fetchData,
    isLoading,
  };
};
